import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Tags, Eye } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { Card, Empty } from "@/pages/portal/event-space/shared";

const LEVELS = [["sector", "Secteur d'activité"], ["expertise", "Expertise"], ["objective", "Objectif de rencontre"]];
const FIELDS = [["job_title", "Poste"], ["company_name", "Entreprise"], ["phone", "Téléphone professionnel"], ["linkedin", "Profil LinkedIn (URL)"]];
const AREAS = [["bio", "Présentation (vous et votre entreprise)"], ["goals", "Mes objectifs pour l'événement"], ["looking_for", "Ce que je recherche comme contact"]];

export default function ProfileSection({ space, slug, reload }) {
  const [p, setP] = useState(null);
  const [tags, setTags] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/event-space/${slug}/profile`).then((r) => setP(r.data)).catch(() => {});
    api.get(`/event-space/${slug}/tags`).then((r) => setTags(r.data)).catch(() => {});
  }, [slug]);

  if (space.state === "not_registered") return <Empty testId="profile-locked">Inscrivez-vous à l'événement pour créer votre profil de networking.</Empty>;
  if (!p) return <div className="text-sm text-neutral-500">Chargement…</div>;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const toggleTag = (lvl, t) => setP((x) => {
    const cur = new Set((x.tags || {})[lvl] || []);
    cur.has(t) ? cur.delete(t) : cur.add(t);
    return { ...x, tags: { ...(x.tags || {}), [lvl]: [...cur] } };
  });
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/event-space/${slug}/profile`, p);
      setP((x) => ({ ...x, completion: r.data.completion }));
      toast.success("Profil enregistré");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };
  const vis = p.visibility || {};

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card title="Mon profil professionnel" className="lg:col-span-2" testId="profile-form">
        <div className="mb-4 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-100"><div className="h-full bg-gradient-to-r from-wine-400 to-wine-600" style={{ width: `${p.completion?.pct || 0}%` }} /></div>
          <span className="text-sm font-bold text-navy" data-testid="profile-pct">{p.completion?.pct || 0} %</span>
        </div>
        <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
          <div><label className="text-[10px] font-bold uppercase text-neutral-500">Photo</label><ImageUpload value={p.photo} onChange={(v) => set("photo", v)} testId="profile-photo" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            {FIELDS.map(([k, l]) => <label key={k} className="text-xs font-semibold text-neutral-600">{l}<input value={p[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-normal" data-testid={`profile-${k}`} /></label>)}
          </div>
        </div>
        <div className="mt-4 grid gap-3">
          {AREAS.map(([k, l]) => <label key={k} className="text-xs font-semibold text-neutral-600">{l}<textarea rows={3} value={p[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-normal" data-testid={`profile-${k}`} /></label>)}
        </div>
      </Card>
      <div className="space-y-5">
        <Card title="Mes tags professionnels" icon={Tags} testId="profile-tags">
          {LEVELS.map(([lvl, label]) => (
            <div key={lvl} className="mb-3">
              <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</div>
              <div className="flex flex-wrap gap-1.5">
                {(tags[lvl] || []).map((t) => {
                  const on = ((p.tags || {})[lvl] || []).includes(t);
                  return <button key={t} type="button" onClick={() => toggleTag(lvl, t)} className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "bg-wine-500 text-white" : "bg-neutral-100 text-neutral-700 hover:bg-wine-50"}`} data-testid={`tag-${lvl}-${t}`}>{t}</button>;
                })}
              </div>
            </div>
          ))}
        </Card>
        <Card title="Confidentialité" icon={Eye} testId="profile-visibility">
          {[["listed", "Apparaître dans l'annuaire des participants"], ["show_linkedin", "Afficher mon LinkedIn"], ["show_phone", "Afficher mon téléphone"], ["show_email", "Afficher mon e-mail"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 py-1 text-sm text-neutral-700"><input type="checkbox" checked={k === "listed" || k === "show_linkedin" ? vis[k] !== false : !!vis[k]} onChange={(e) => set("visibility", { ...vis, [k]: e.target.checked })} data-testid={`vis-${k}`} />{l}</label>
          ))}
        </Card>
        <button onClick={save} disabled={saving} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-60" data-testid="profile-save"><Save className="h-4 w-4" />{saving ? "Enregistrement…" : "Enregistrer mon profil"}</button>
      </div>
    </div>
  );
}
